import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import toast from "react-hot-toast";

import { useGetEventsQuery } from "../../store/services/events";
import { useGetEventTypesQuery } from "../../store/services/events-types";
import { useCreateBenefitFeaturedEventMutation } from "../../store/services/benefits";
import { useDebounce } from "../../hooks/useDebounce";
import { Event } from "../../models/events";
import { DateRangeFilter } from "../../components/Filters/DateRangeFilter";
import { toEventDateParam } from "../../store/slices/eventsFiltersSlice";
import { AppDialog, StatusBadge, emptyMessage } from "../../components/ui";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";

interface AddFeaturedEventDialogProps {
  visible: boolean;
  onHide: () => void;
  /** ids ya presentes en la lista curada (activos o pausados) para bloquear duplicados. */
  existingEventIds: number[];
}

/**
 * DataTable memoiza cada celda comparando `rowData` (ver `cellMemo` en la doc de
 * PrimeReact): un cambio en closures externas al row (como `existingEventIds`)
 * NO alcanza para que la celda se re-renderice. Por eso el estado "ya destacado"
 * se mezcla directamente en el objeto de la fila.
 */
type EventRow = Event & { isFeatured: boolean };

const RESULTS_LIMIT = 20;
const SEARCH_DEBOUNCE_MS = 500;

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const AddFeaturedEventDialog = ({
  visible,
  onHide,
  existingEventIds,
}: AddFeaturedEventDialogProps) => {
  const [eventId, setEventId] = React.useState<number | null>(null);
  const [name, setName] = React.useState("");
  const [typeId, setTypeId] = React.useState<number | null>(null);
  const [subTypeId, setSubTypeId] = React.useState<number | null>(null);
  const [dateFrom, setDateFrom] = React.useState<Date | null>(getToday);
  const [dateTo, setDateTo] = React.useState<Date | null>(null);
  const [addingEventId, setAddingEventId] = React.useState<number | null>(null);
  // La lista curada (y por lo tanto `existingEventIds`) se actualiza recién
  // cuando el refetch disparado por la invalidación de tags vuelve del server;
  // hasta entonces esta copia local evita que la fila quede mostrando "Agregar"
  // tras un alta exitosa.
  const [justAddedIds, setJustAddedIds] = React.useState<Set<number>>(new Set());

  const { data: eventTypes, isLoading: isLoadingTypes } = useGetEventTypesQuery();

  const subTypeOptions = React.useMemo(
    () => eventTypes?.find((type) => type.eventTypeId === typeId)?.subTypes ?? [],
    [eventTypes, typeId]
  );

  const handleTypeChange = (value: number | null) => {
    setTypeId(value);
    setSubTypeId(null);
  };

  // Se debouncean todos los filtros juntos para que la búsqueda dispare sola
  // mientras el usuario escribe/selecciona, sin necesitar salir del campo.
  const filters = React.useMemo(
    () => ({ eventId, name, typeId, subTypeId, dateFrom, dateTo }),
    [eventId, name, typeId, subTypeId, dateFrom, dateTo]
  );
  const debouncedFilters = useDebounce(filters, SEARCH_DEBOUNCE_MS);

  const { data: eventsPage, isLoading, isFetching } = useGetEventsQuery(
    {
      id: debouncedFilters.eventId ?? undefined,
      name: debouncedFilters.name || undefined,
      typeId: debouncedFilters.typeId ?? undefined,
      subTypeId: debouncedFilters.subTypeId ?? undefined,
      startDate: debouncedFilters.dateFrom
        ? toEventDateParam(debouncedFilters.dateFrom)
        : undefined,
      endDate: debouncedFilters.dateTo
        ? toEventDateParam(debouncedFilters.dateTo)
        : undefined,
      show: "all",
      limit: RESULTS_LIMIT,
      sortBy: "startDate",
      sortOrder: "desc",
    },
    { skip: !visible }
  );
  const events = eventsPage?.events;

  const [addEvent, { isLoading: isAdding }] = useCreateBenefitFeaturedEventMutation();

  // `isFeatured` viaja dentro de la propia fila (no como closure externa) para
  // que el cellMemo de PrimeReact detecte el cambio y re-renderice esa celda.
  const rows: EventRow[] = React.useMemo(
    () =>
      (events ?? []).map((event) => ({
        ...event,
        isFeatured: existingEventIds.includes(event.id) || justAddedIds.has(event.id),
      })),
    [events, existingEventIds, justAddedIds]
  );

  const handleAdd = (event: Event) => {
    setAddingEventId(event.id);
    addEvent({ eventId: event.id })
      .unwrap()
      .then(() => {
        setJustAddedIds((prev) => new Set(prev).add(event.id));
        toast.success(`"${event.name}" agregado a destacados`);
      })
      .catch((err) => {
        toast.error(getAdminFacingApiErrorMessage(err, "No se pudo agregar el evento"));
      })
      .finally(() => setAddingEventId(null));
  };

  const resetFilters = () => {
    setEventId(null);
    setName("");
    setTypeId(null);
    setSubTypeId(null);
    setDateFrom(getToday());
    setDateTo(null);
  };

  const hasFilters = Boolean(eventId || name || typeId || subTypeId || dateTo);

  const handleHide = () => {
    resetFilters();
    setJustAddedIds(new Set());
    onHide();
  };

  const dateBodyTemplate = (event: EventRow) =>
    event.startDate
      ? new Date(event.startDate).toLocaleDateString("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  const actionsBodyTemplate = (event: EventRow) =>
    event.isFeatured ? (
      <StatusBadge label="Ya destacado" tone="success" />
    ) : (
      // `+` circular con relleno de marca, igual que el resto de los `+`.
      <Button
        icon="pi pi-plus"
        rounded
        size="small"
        aria-label="Agregar a destacados"
        tooltip="Agregar a destacados"
        tooltipOptions={{ position: "top" }}
        onClick={() => handleAdd(event)}
        loading={isAdding && addingEventId === event.id}
      />
    );

  return (
    <AppDialog
      variant="detail"
      visible={visible}
      onHide={handleHide}
      header="Agregar evento destacado"
      style={{ width: "75rem", maxWidth: "95vw" }}
    >
      {/* Mismo patrón que los filtros del listado de eventos (§ INC-02). */}
      <div className="grid mt-3 row-gap-3 mb-3">
        <div className="col-12 md:col-3">
          <FloatLabel className="w-full">
            <InputText
              id="filterName"
              className="w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <label htmlFor="filterName">Nombre</label>
          </FloatLabel>
        </div>
        <div className="col-12 md:col-3">
          <FloatLabel className="w-full">
            <InputNumber
              inputId="filterId"
              className="w-full"
              inputClassName="w-full"
              value={eventId}
              useGrouping={false}
              onChange={(e: InputNumberChangeEvent) => setEventId(e.value ?? null)}
            />
            <label htmlFor="filterId">ID</label>
          </FloatLabel>
        </div>
        <div className="col-12 md:col-3">
          <FloatLabel className="w-full">
            <Dropdown
              inputId="filterType"
              className="w-full"
              value={typeId}
              options={eventTypes ?? []}
              optionLabel="eventTypeName"
              optionValue="eventTypeId"
              loading={isLoadingTypes}
              showClear
              onChange={(e) => handleTypeChange(e.value ?? null)}
            />
            <label htmlFor="filterType">Tipo</label>
          </FloatLabel>
        </div>
        <div className="col-12 md:col-3">
          <FloatLabel className="w-full">
            <Dropdown
              inputId="filterSubType"
              className="w-full"
              value={subTypeId}
              options={subTypeOptions}
              optionLabel="eventSubTypeName"
              optionValue="eventSubTypeId"
              showClear
              emptyMessage="Seleccioná un tipo primero"
              onChange={(e) => setSubTypeId(e.value ?? null)}
            />
            <label htmlFor="filterSubType">Sub-tipo</label>
          </FloatLabel>
        </div>
        <DateRangeFilter
          variant="filter"
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          startDateId="featuredEventFrom"
          endDateId="featuredEventTo"
          labelFrom="Fecha desde"
          labelTo="Fecha hasta"
          columnClassName="col-12 md:col-3"
        />
        <div className="col-12 md:col flex justify-content-end align-items-center">
          <Button
            type="button"
            label="Limpiar filtros"
            icon="pi pi-filter-slash"
            severity="secondary"
            outlined
            size="small"
            onClick={resetFilters}
          />
        </div>
      </div>

      <DataTable
        value={rows}
        loading={isLoading || isFetching}
        dataKey="id"
        emptyMessage={emptyMessage("eventos", hasFilters)}
        paginator
        rows={10}
      >
        <Column field="id" header="ID" style={{ width: "5rem" }} />
        <Column field="name" header="Nombre" style={{ minWidth: "14rem" }} />
        <Column field="eventTypeName" header="Tipo" style={{ minWidth: "10rem" }} />
        <Column field="eventSubTypeName" header="Sub-tipo" style={{ minWidth: "10rem" }} />
        <Column header="Fecha" body={dateBodyTemplate} style={{ width: "9rem" }} />
        <Column header="" body={actionsBodyTemplate} style={{ width: "10rem" }} exportable={false} />
      </DataTable>
      {(events?.length ?? 0) >= RESULTS_LIMIT && (
        <small className="text-color-secondary block mt-2">
          Mostrando los primeros {RESULTS_LIMIT} resultados. Usá los filtros para acotar la
          búsqueda.
        </small>
      )}
    </AppDialog>
  );
};
